import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import api from '../lib/api';
import {
  AgencyAccent,
  BackdropMood,
  SurfaceStyle,
  applyAgencyAccent,
  applyBackdrop,
  applySurface,
  readStoredAgencyAccent,
  readStoredBackdrop,
  readStoredSurface,
} from '../lib/appearance';

const SURFACES: { id: SurfaceStyle; label: string }[] = [
  { id: 'frosted', label: 'Frosted' },
  { id: 'crystal', label: 'Crystal' },
  { id: 'solid', label: 'Solid' },
];

const BACKDROPS: { id: BackdropMood; label: string }[] = [
  { id: 'aurora', label: 'Aurora' },
  { id: 'candy', label: 'Candy' },
  { id: 'mono', label: 'Mono' },
];

const ACCENTS: { id: AgencyAccent; label: string }[] = [
  { id: 'violet', label: 'Violet' },
  { id: 'ocean', label: 'Ocean' },
  { id: 'sunset', label: 'Sunset' },
  { id: 'jade', label: 'Jade' },
];

type Props = {
  showAgencyAccent?: boolean;
  compact?: boolean;
};

export default function AppearanceSettings({ showAgencyAccent = false, compact = false }: Props) {
  const user = useAuthStore((s) => s.user);
  const [surface, setSurface] = useState<SurfaceStyle>(() =>
    typeof window !== 'undefined' ? readStoredSurface() : 'frosted'
  );
  const [backdrop, setBackdrop] = useState<BackdropMood>(() =>
    typeof window !== 'undefined' ? readStoredBackdrop() : 'aurora'
  );
  const [accent, setAccent] = useState<AgencyAccent>(() =>
    typeof window !== 'undefined' ? readStoredAgencyAccent() : 'violet'
  );

  useEffect(() => {
    setSurface(readStoredSurface());
    setBackdrop(readStoredBackdrop());
    setAccent(readStoredAgencyAccent());
  }, [user?.id]);

  const persist = useCallback(
    async (patch: Record<string, string>) => {
      if (!user) return;
      try {
        await api.put('/users/me/settings', patch);
      } catch {
        /* local still applied */
      }
    },
    [user]
  );

  const onSurface = (v: SurfaceStyle) => {
    setSurface(v);
    applySurface(v);
    void persist({ surfaceStyle: v });
  };
  const onBackdrop = (v: BackdropMood) => {
    setBackdrop(v);
    applyBackdrop(v);
    void persist({ backdrop: v });
  };
  const onAccent = (v: AgencyAccent) => {
    setAccent(v);
    applyAgencyAccent(v);
    void persist({ agencyAccent: v });
  };

  const seg =
    'inline-flex flex-wrap gap-1 rounded-xl border border-glass bg-glass-1 p-1';
  const btn = (on: boolean) =>
    `px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
      on
        ? 'bg-accent2 text-white shadow-accent-glow'
        : 'text-glass-text-2 hover:bg-glass-3 hover:text-glass-text'
    }`;

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {!compact ? (
        <div>
          <h3 className="text-sm font-semibold text-glass-text">Appearance</h3>
          <p className="text-xs text-glass-text-3 mt-0.5">
            Surface material, backdrop mood{showAgencyAccent ? ', and accent palette' : ''}.
          </p>
        </div>
      ) : null}

      <div>
        <p className="text-[0.68rem] font-bold uppercase tracking-wider text-glass-text-3 mb-1.5">
          Surface
        </p>
        <div className={seg}>
          {SURFACES.map((s) => (
            <button key={s.id} type="button" className={btn(surface === s.id)} onClick={() => onSurface(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[0.68rem] font-bold uppercase tracking-wider text-glass-text-3 mb-1.5">
          Backdrop
        </p>
        <div className={seg}>
          {BACKDROPS.map((s) => (
            <button key={s.id} type="button" className={btn(backdrop === s.id)} onClick={() => onBackdrop(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {showAgencyAccent ? (
        <div>
          <p className="text-[0.68rem] font-bold uppercase tracking-wider text-glass-text-3 mb-1.5">
            Accent
          </p>
          <div className={seg}>
            {ACCENTS.map((s) => (
              <button key={s.id} type="button" className={btn(accent === s.id)} onClick={() => onAccent(s.id)}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
